// ResultadosEvento — post-closure TEAM results + ranking for an event.
// Mirrors sophi-bot's group summary (postGroupSummary): top seller/tipper,
// team totals + 10% tip goal, per-role ranking by gross tips. SALES & TIPS
// ONLY — no salaries (each person's pay arrives as a private Sophi DM). The
// viewer's own row is highlighted. Polls while the closure is still finalizing.

import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { getEventResults, type EventResults, type ResultRow } from '../lib/api';

const POLL_INTERVAL_MS = 4000;

interface ResultadosEventoProps {
  eventId: string;
  eventTitle?: string;
  onClose: () => void;
}

export function ResultadosEvento({ eventId, eventTitle, onClose }: ResultadosEventoProps) {
  const [data, setData] = useState<EventResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const r = await getEventResults(eventId);
      setData(r);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [eventId]);

  // While results aren't ready (finalize still running), poll a few seconds.
  useEffect(() => {
    if (!data || data.ready) return;
    const t = setTimeout(load, POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [data]);

  if (loading) {
    return (
      <Shell title={eventTitle || eventId} onClose={onClose}>
        <div class="loading"><span class="spinner" /></div>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell title={eventTitle || eventId} onClose={onClose}>
        <div class="mireporte-error">⚠️ {error === 'not_a_member' ? 'No trabajaste este evento.' : (error || 'No pudimos cargar los resultados.')}</div>
        <button class="btn primary" onClick={onClose}>Volver</button>
      </Shell>
    );
  }

  if (!data.ready) {
    return (
      <Shell title={data.evento.title} onClose={onClose}>
        <div class="resultados-pending">
          <div class="resultados-pending-spin"><span class="spinner" /></div>
          <h3>⏳ Calculando resultados…</h3>
          <p class="mireporte-meta">Los resultados aparecen apenas termine el cierre del evento.</p>
        </div>
      </Shell>
    );
  }

  const { evento, totals, top, camareros = [], supervisores = [], bartenders = [], otros = [] } = data;
  const tipTarget = data.tipTarget ?? 10;
  const distLabel = evento.distribucion === 'Todos_Los_Roles' ? '🔄 Todos los roles'
    : evento.distribucion === 'Solo_Camareros' ? '👥 Solo camareros'
    : evento.distribucion === 'Pool_Total' ? '🤝 Pool total (partes iguales)' : null;

  return (
    <Shell title={evento.title} onClose={onClose}>
      <div class="resultados-sub">
        {evento.cliente && <span>{evento.cliente}</span>}
        {distLabel && <span class="resultados-dist">{distLabel}</span>}
      </div>

      {/* Top performers */}
      {(top?.ventas || top?.propinas) && (
        <div class="resultados-top">
          {top?.ventas && (
            <div class="resultados-top-card">
              <div class="resultados-top-label">🏆 Mayor ventas</div>
              <div class="resultados-top-name">{top.ventas.nombre}</div>
              <div class="resultados-top-val">RD$ {fmt(top.ventas.valor)}</div>
            </div>
          )}
          {top?.propinas && (
            <div class="resultados-top-card">
              <div class="resultados-top-label">💰 Mayor propinas</div>
              <div class="resultados-top-name">{top.propinas.nombre}</div>
              <div class="resultados-top-val">RD$ {fmt(top.propinas.valor)}</div>
            </div>
          )}
        </div>
      )}

      {/* Team totals */}
      {totals && (
        <div class="resultados-totales">
          <div class="resultados-totales-head">💰 Totales del equipo</div>
          <div class="resultados-totales-row"><span>Ventas</span><b>RD$ {fmt(totals.ventas)}</b></div>
          <div class="resultados-totales-row"><span>Propinas</span><b>RD$ {fmt(totals.propinas)}</b></div>
          <div class={`resultados-meta ${totals.goalMet ? 'ok' : 'warn'}`}>
            📈 {totals.pctEquipo}% propinas/ventas — {totals.goalMet ? '¡Meta alcanzada! 🎉' : `meta: ${tipTarget}% ⚠️`}
          </div>
        </div>
      )}

      {/* Camareros ranking */}
      {camareros.length > 0 && (
        <RoleSection title="👥 Camareros — ranking de propinas" rows={camareros} ranked tipTarget={tipTarget} />
      )}
      {supervisores.length > 0 && (
        <RoleSection title="🎩 Supervisor" rows={supervisores} tipTarget={tipTarget} />
      )}
      {bartenders.length > 0 && (
        <RoleSection title="🍸 Bartenders" rows={bartenders} ranked tipTarget={tipTarget} />
      )}
      {otros.length > 0 && (
        <RoleSection title="🧰 Otros" rows={otros} tipTarget={tipTarget} />
      )}

      <p class="resultados-dm-note">💬 Cada quien recibe su pago y desglose completo por DM de Sophi.</p>
    </Shell>
  );
}

function RoleSection({ title, rows, ranked, tipTarget }: { title: string; rows: ResultRow[]; ranked?: boolean; tipTarget: number }) {
  return (
    <div class="resultados-role">
      <div class="resultados-role-head">{title}</div>
      <ul class="resultados-list">
        {rows.map((r, idx) => (
          <li key={r.staffId || r.nombre} class={`resultados-row${r.isMe ? ' me' : ''}`}>
            <div class="resultados-row-head">
              <span class="resultados-rank">{ranked ? medal(idx) : '•'}</span>
              <span class="resultados-name">
                {r.nombre}{r.isMe && <span class="resultados-you"> · Tú</span>}
              </span>
              {r.cumpleMeta && r.pctPropinas >= tipTarget && <span class="resultados-check">✅</span>}
            </div>
            <div class="resultados-row-meta">
              ventas RD$ {fmt(r.ventas)} · generó RD$ {fmt(r.propBrutas)} en propinas
              {r.ventas > 0 && <> ({r.pctPropinas}%)</>}
            </div>
            <div class="resultados-row-neta">
              💵 Recibe <b>RD$ {fmt(r.propNeta)}</b>
              {r.fueraDelPool && <span class="resultados-pool-note"> · se queda con las suyas (fuera del pool)</span>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Shell({ title, onClose, children }: { title: string; onClose: () => void; children: ComponentChildren }) {
  return (
    <div class="event-chat-screen">
      <header class="event-chat-header">
        <button class="event-chat-back" onClick={onClose} aria-label="Cerrar">←</button>
        <div class="event-chat-titles">
          <div class="event-chat-title">Resultados · {title}</div>
          <div class="event-chat-sub">Resumen del equipo</div>
        </div>
      </header>
      <div class="mireporte-body">{children}</div>
    </div>
  );
}

function medal(idx: number): string {
  return idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`;
}

function fmt(n: number): string {
  return Math.round(n || 0).toLocaleString('es-DO');
}
